import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Trash2,
  TrendingUp,
  Radar,
  ArrowUpRight,
  MessageSquare,
  Briefcase,
  Home,
  Star,
} from "lucide-react";
import SubscribeDialog from "@/components/site/SubscribeDialog";
import RemovalCheckoutDialog from "@/components/site/RemovalCheckoutDialog";
import QuoteRequestDialog from "@/components/site/QuoteRequestDialog";

const services = [
  {
    icon: Trash2,
    title: "Google Review Removal",
    tagline: "$195 per review · Pay after removal",
    body:
      "We identify negative reviews that violate Google's content policies — and file for their removal through Google's own reporting workflows. If it can't be removed, you don't pay a cent.",
    bullets: [
      "Fake / never-a-customer reviews",
      "Off-topic, hateful, or spam",
      "Conflicts of interest & competitor sabotage",
    ],
    span: "lg:col-span-8",
    accent: "emerald",
    removal: {
      label: "Report a review — $195",
      testId: "services-cta-removal",
    },
  },
  {
    icon: Radar,
    title: "Monitoring & Maintenance",
    tagline: "$45/month",
    body:
      "24/7 watch on new reviews with real-time alerts, monthly reports, and immediate escalation of any new policy-violating content.",
    bullets: [
      "Instant alerts on new negatives",
      "Monthly reputation report",
      "Priority queue for removals",
    ],
    span: "lg:col-span-4",
    accent: "emerald",
  },
  {
    icon: MessageSquare,
    title: "Reddit Review Removal",
    tagline: "Custom quote · Pay after removal",
    body:
      "Ethical removal of Reddit comments and posts that violate subreddit rules or Reddit's Content Policy — harassment, doxxing, brigading, or off-topic attacks against your business.",
    bullets: [
      "Content-policy & subreddit-rules mapping",
      "Direct escalation to mod teams & Reddit admins",
      "Zero fake upvote manipulation",
    ],
    span: "lg:col-span-6",
    accent: "gold",
    quote: {
      platform: "Reddit",
      label: "Contact for quote",
      testId: "services-cta-reddit",
    },
  },
  {
    icon: Briefcase,
    title: "Glassdoor Review Removal",
    tagline: "Custom quote · Pay after removal",
    body:
      "Remove Glassdoor reviews that violate Community Guidelines — reviews from people who never worked at your company, defamatory attacks on named executives, or leaked confidential information.",
    bullets: [
      "Community-Guidelines-mapped filings",
      "Documentation package for Glassdoor Trust team",
      "Employer response strategy",
    ],
    span: "lg:col-span-6",
    accent: "gold",
    quote: {
      platform: "Glassdoor",
      label: "Contact for quote",
      testId: "services-cta-glassdoor",
    },
  },
  {
    icon: Home,
    title: "Airbnb Review Removal",
    tagline: "Custom quote · Pay after removal",
    body:
      "For hosts drowning under unfair Airbnb reviews. We appeal reviews that violate Airbnb's Review Policy — retaliation, off-platform disputes, or content unrelated to the actual stay.",
    bullets: [
      "Review Policy mapping (retaliation, extortion, biased)",
      "Superhost-eligible appeal workflow",
      "Guest-communication evidence package",
    ],
    span: "lg:col-span-6",
    accent: "gold",
    quote: {
      platform: "Airbnb",
      label: "Contact for quote",
      testId: "services-cta-airbnb",
    },
  },
  {
    icon: Star,
    title: "Trustpilot Review Removal",
    tagline: "Custom quote · Pay after removal",
    body:
      "Trustpilot has strict Guidelines for Reviewers. We file removals for reviews from non-customers, promotional/off-topic content, or reviews that violate the harmful-language policy.",
    bullets: [
      "Guidelines-for-Reviewers mapped filings",
      "Trustpilot Content Integrity Team escalation",
      "Verification-evidence bundle",
    ],
    span: "lg:col-span-6",
    accent: "gold",
    quote: {
      platform: "Trustpilot",
      label: "Contact for quote",
      testId: "services-cta-trustpilot",
    },
  },
  {
    icon: TrendingUp,
    title: "Ranking Improvement",
    tagline: "Local visibility",
    body:
      "Google Business Profile optimization, category refinement, and review-generation workflows that lift your local pack rankings.",
    bullets: ["GBP audit & fixes", "Keyword-aligned categories", "Owner-response strategy"],
    span: "lg:col-span-12",
    accent: "gold",
  },
];

function ServiceCard({ s, i, onOpenSubscribe, onOpenRemoval, onOpenQuote }) {
  const ref = useRef(null);
  const isEmerald = s.accent === "emerald";

  const onSubscribe = () => {
    if (!s.subscribe) return;
    onOpenSubscribe({
      packageId: s.subscribe.packageId,
      label: s.subscribe.dialogLabel || s.title,
      priceLabel: s.subscribe.priceLabel,
      priceUsd: s.subscribe.priceUsd,
    });
  };

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55, delay: i * 0.1 }}
      className={`group relative rounded-3xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors overflow-hidden ${s.span}`}
      style={{
        "--mx": "50%",
        "--my": "50%",
      }}
    >
      {/* Cursor-follow spotlight */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: isEmerald
            ? "radial-gradient(500px circle at var(--mx) var(--my), rgba(16,185,129,0.14), transparent 40%)"
            : "radial-gradient(500px circle at var(--mx) var(--my), rgba(212,175,55,0.14), transparent 40%)",
        }}
      />

      <div className="relative p-8 lg:p-10">
        <div className="flex items-start justify-between gap-4">
          <div
            className={`w-12 h-12 grid place-items-center rounded-2xl border transition-transform group-hover:-rotate-3 ${
              isEmerald
                ? "bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]"
                : "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]"
            }`}
          >
            <s.icon className="w-5 h-5" />
          </div>
          <div className="text-xs text-white/40 group-hover:text-white/70 transition-colors flex items-center gap-1">
            Learn more
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">{s.title}</h3>
          <span
            className={`text-[11px] uppercase tracking-widest px-2 py-1 rounded-full border ${
              isEmerald
                ? "border-[#10B981]/30 text-[#10B981]/90 bg-[#10B981]/5"
                : "border-[#D4AF37]/30 text-[#D4AF37]/90 bg-[#D4AF37]/5"
            }`}
          >
            {s.tagline}
          </span>
        </div>

        <p className="mt-4 text-white/60 text-sm leading-relaxed max-w-2xl">{s.body}</p>

        <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
          {s.bullets.map((b) => (
            <li key={b} className="text-sm text-white/50 flex items-center gap-2">
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  isEmerald ? "bg-[#10B981]" : "bg-[#D4AF37]"
                }`}
              />
              {b}
            </li>
          ))}
        </ul>

        {s.subscribe && (
          <button
            type="button"
            onClick={onSubscribe}
            data-testid={s.subscribe.testId}
            className="mt-8 relative inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full font-semibold text-sm transition-colors overflow-hidden bg-[#D4AF37] hover:bg-[#c19b2e] text-[#0A0F1C] shadow-lg shadow-[#D4AF37]/25"
          >
            {s.subscribe.label}
          </button>
        )}
        {s.removal && (
          <button
            type="button"
            onClick={onOpenRemoval}
            data-testid={s.removal.testId}
            className="mt-8 relative inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full font-semibold text-sm transition-colors overflow-hidden bg-[#10B981] hover:bg-[#0ea774] text-white shadow-lg shadow-emerald-500/25"
          >
            {s.removal.label}
          </button>
        )}
        {s.quote && (
          <button
            type="button"
            onClick={() => onOpenQuote(s.quote.platform)}
            data-testid={s.quote.testId}
            className="mt-8 relative inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full font-semibold text-sm transition-colors overflow-hidden bg-[#D4AF37] hover:bg-[#c19b2e] text-[#0A0F1C] shadow-lg shadow-[#D4AF37]/25"
          >
            {s.quote.label}
          </button>
        )}
      </div>

      <div
        className={`pointer-events-none absolute -bottom-16 -right-10 w-56 h-56 rounded-full blur-3xl ${
          isEmerald ? "bg-[#10B981]/10" : "bg-[#D4AF37]/10"
        }`}
      />
    </motion.div>
  );
}

export default function Services() {
  const [subscribe, setSubscribe] = useState(null);
  const [removal, setRemoval] = useState(false);
  const [quotePlatform, setQuotePlatform] = useState(null);

  return (
    <section id="services" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
              Services
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-tight">
              Everything you need to protect the reputation you've earned.
            </h2>
          </div>
          <p className="text-sm text-white/50 max-w-sm">
            Purpose-built for local businesses drowning in unfair reviews. Every service is
            performance-priced or policy-compliant — never both compromised.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {services.map((s, i) => (
            <ServiceCard
              key={s.title}
              s={s}
              i={i}
              onOpenSubscribe={setSubscribe}
              onOpenRemoval={() => setRemoval(true)}
              onOpenQuote={setQuotePlatform}
            />
          ))}
        </div>
      </div>

      <SubscribeDialog
        open={!!subscribe}
        onClose={() => setSubscribe(null)}
        packageId={subscribe?.packageId}
        label={subscribe?.label}
        priceLabel={subscribe?.priceLabel}
        priceUsd={subscribe?.priceUsd}
      />
      <RemovalCheckoutDialog
        open={removal}
        onClose={() => setRemoval(false)}
      />
      <QuoteRequestDialog
        open={!!quotePlatform}
        platform={quotePlatform}
        onClose={() => setQuotePlatform(null)}
      />
    </section>
  );
}
